export interface Job {
  id: string;
  task_type?: string;
  status: 'pending' | 'queued' | 'processing' | 'success' | 'failed' | 'unknown';
  source_type?: string;
  source?: string;
  source_id?: string
  message?: string;
  error?: string;
  created_at?: string;
  updated_at?: string;
}

export type SyncSchedule = 'off' | 'hourly' | 'daily' | 'weekly';

export interface Source {
    id: string;
    source: string;
    source_type: string;
    config: Record<string, any>;
    sync_schedule: SyncSchedule;
    next_sync_at: string | null;
    created_at: string;
    updated_at: string;
    file_count: number;
    searchable?: boolean;  // can be searched, so it runs standing searches
    live?: boolean;  // searched only while its owner asks Navaris; keeps nothing and never syncs
}

export interface SourceQuery {
    id: string;
    source_id: string;
    text: string;
    section_id: string | null;
    origin: 'default' | 'person';
    created_by: string | null;
    last_run_at: string | null;
    created_at: string;
    hits: number;
    read: number;
}

export interface MarketPage {
    url: string;
    section: string;
    why: string;
    status: 'proposed' | 'active' | 'dismissed';
    added_by?: string | null;
}

export interface MarketView {
    watchlist: MarketPage[];
    sections: string[];
    ready: boolean;
}

export interface FileLinkedTo {
    definition_id: string;
    document_type_name: string;
    section_id: string;
    section_name: string;
    version: number | null;  // null for folder links (no pinned version)
}

export interface FileCandidateFor {
    definition_id: string;
    document_type_name: string;
    section_id: string | null;
    section_name: string | null;
    score: number;
}

export interface UsageRecord {
    id: string;
    usage_date: string;      // YYYY-MM-DD (UTC)
    action: string;
    model: string;
    input_tokens: number;      // full input total, cache included
    cache_read_tokens: number; // of input_tokens, read from the prompt cache (~0.1x)
    cache_write_tokens: number;// of input_tokens, written to the prompt cache (~1.25x)
    output_tokens: number;
    total_tokens: number;
    call_count: number;
}

export interface Agent {
    key: string;
    name: string;
    description: string;
    default: boolean;
}

export interface SuggestedQuestion {
    text: string;
    /** null = Navaris answers it without a skill. */
    skill: string | null;
}

export interface FiledDocument {
    file_id: string;
    version: number;
    name: string;
    definition_id: string;
}

export interface AgentChatSummary {
    id: string;
    /** The skill in use; null = none. */
    skill: string | null;
    title?: string | null;
    /** The AI assistant the conversation came from; null = the web app. */
    origin?: string | null;
    created_at: string;
    updated_at: string;
}

// `version` pins a file version; null follows the latest.
export interface ChatAttachment {
    id: string;
    chat_id: string;
    file_id: string;
    version?: number | null;
    definition_id?: string | null;
    label?: string | null;
    filename?: string | null;
    created_at: string;
}

export interface AgentMessage {
    id: string;
    chat_id: string;
    role: 'user' | 'assistant' | 'activity' | 'document' | 'proposal' | 'skill' | 'question' | 'navigation' | 'trigger';
    /** The skill a user message named, the one in use when the agent wrote, or the one a switch went to. */
    skill?: string | null;
    /** A document's title, the question a proposal answers, or a question asked. */
    title?: string | null;
    content: string;
    /** A document's Word file, under Documents > Navaris. */
    file_id?: string | null;
    position: number;
    created_at: string;
}

export interface TimelineEvent {
    id: string;
    at: string;                       // ISO8601 (business/effective time)
    event: 'began' | 'ended';
    kind: 'fact' | 'relationship';
    type: string;                     // resource or relation name
    entity: string;
    title: string;
    detail?: string | null;
    source?: KnowledgeSource | null;
}

export interface BrowseFolder {
    name: string;
    path: string;
    linked: boolean;
    linked_count?: number;
}

export interface KnowledgeFieldSchema {
    type?: string;
    format?: string;
    enum?: string[];
    description?: string;
    $ref?: string;
    [k: string]: any;
}

// `conflict` is present when a person's entry overrides a system that now disagrees.
export interface FieldOrigin {
    kind: 'derived' | 'computed' | 'extracted' | 'stated';
    source: KnowledgeSource | null;
    /** A computed figure's: the contributors to the lines it read. */
    sources?: KnowledgeSource[];
    /** Who contributed it, as one label; a computed figure's joins its sources. */
    origin_name?: string | null;
    figure?: {
        key: string;
        concept: string;
        organization: string;
        period: ModelPeriod;
        value: { amount?: number; unit?: string };
    } | null;
    conflict?: {
        kind: 'derived' | 'computed' | 'extracted';
        value: unknown;
        source: KnowledgeSource | null;
        origin_name?: string | null;
    } | null;
}

export interface OutstandingField {
    id: string;
    label: string;
    section_id: string | null;
    group_id: string | null;
    input_type: string;
    options?: { value: string; label: string }[] | null;
    help_text?: string | null;
    guidance?: string | null;
    /** Help drafting an answer is offered, with the skill it names, if any. */
    draft?: { skill: string | null } | null;
    binds?: Record<string, unknown> | null;
    status: 'derived' | 'extracted' | 'stated' | 'unknown';
    value: unknown;
    origin: FieldOrigin | null;
    document_impact?: AnswerImpact[];
}

// A line of the model a business area shows on its Summary.
export interface SummaryFigure {
    id: string;
    section_id: string;
    grain: string | null;
    concept: {
        id: string; name: string; unit: string; description: string;
        kind: 'ledger' | 'metric' | 'measure';
        better?: 'higher' | 'lower' | null;
    };
    input_type: string;
    // A computed or broken-down line is corrected through its inputs, never entered.
    editable: boolean;
    status: FieldOrigin['kind'] | 'unknown';
    value: number | null;
    origin: FieldOrigin | null;
    target: {
        key: string;
        value: { amount?: number; unit?: string };
        period: ModelPeriod;
        kind: FieldOrigin['kind'];
        status: 'ahead' | 'behind' | null;
    } | null;
    // Broken down by its dimensions (who holds what) or by group company, as at its latest date.
    breakdown: {
        period: ModelPeriod;
        dimensions: { id: string; name: string }[];
        rows: { members: Record<string, string>; value: { amount?: number; unit?: string };
                kind: FieldOrigin['kind']; origin: FieldOrigin }[];
    } | null;
    // "" when the record does not know the currency yet.
    currency: string | null;
}

// What's Next for a summary figure the record lacks: what would produce it.
export interface FigureInput {
    id: string;
    kind: 'connect' | 'map' | 'enter' | 'document';
    section_id: string;
    title: string;
    description: string;
    figures: string[];
    concept?: string;
    grain?: string | null;
    input_type?: string;
    currency?: string | null;
}

export type TaskStatus = 'to_do' | 'in_progress' | 'done';

// Something to do on a business area's What's Next, usually set by an agent.
export interface Task {
    id: string;
    title: string;
    description: string | null;
    notes: string | null;
    status: TaskStatus;
    section_id: string | null;
    due_on: string | null;
    milestone_id: string | null;
    created_by: string | null;
    completed_at: string | null;
    created_at: string;
    updated_at: string;
}

export type PlanStatus = 'draft' | 'active' | 'done' | 'dropped';
export type MilestoneStatus = 'planned' | 'in_progress' | 'achieved' | 'missed' | 'dropped';

// How far a milestone measured on the Model has got: the latest actual figure.
export interface MilestoneProgress {
    current: number | null;
    as_at: string | null;
    met: boolean;
}

// One result a plan commits to. With a `concept` it is measured on the Model.
export interface Milestone {
    id: string;
    plan_id: string;
    name: string;
    description: string | null;
    concept: string | null;
    target: number | null;
    unit: string | null;
    due_on: string | null;
    // Its own date, else its plan's.
    due: string;
    status: MilestoneStatus;
    // The stored status, or achieved / overdue as read from the Model and the date.
    state: MilestoneStatus | 'overdue';
    achieved_on: string | null;
    position: number;
    progress: MilestoneProgress | null;
    task_ids: string[];
    created_by: string | null;
}

// What the business has committed to reach by a date.
export interface Plan {
    id: string;
    name: string;
    description: string | null;
    due_on: string;
    status: PlanStatus;
    section_id: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    milestones: Milestone[];
}

// A line of the Model a milestone can be measured on.
export interface PlanMetric {
    id: string;
    name: string;
    unit: string;
    better: 'higher' | 'lower' | null;
}

export interface KnowledgeLabel {
    label: string;
    name: string;
    description?: string;
    shape_version: number;
    shape: {
        properties?: Record<string, KnowledgeFieldSchema>;
        required?: string[];
        anyOf?: { required: string[] }[];
        [k: string]: any;
    };
}

export interface KnowledgeRelationshipType {
    type: string;
    from: string | string[];
    to: string | string[];
    description?: string;
    properties?: { properties?: Record<string, KnowledgeFieldSchema>; [k: string]: any };
    count?: number;
}

export interface KnowledgeSchema {
    labels: KnowledgeLabel[];
    relationships: KnowledgeRelationshipType[];
    $defs: Record<string, any>;
}

export interface KnowledgeLabelSummary {
    label: string;
    name: string;
    count: number;
    // Records only a document's pending claims would create.
    pending?: number;
}

// Who contributed something on the record: a connected source or a document, which
// have pages to link to, or a person, directly or through a chat with the agent.
export interface KnowledgeSource {
    name?: string | null;
    kind: 'source' | 'document' | 'person' | 'chat';
    id: string;
}

export interface KnowledgeNode {
    label: string;
    key: string;
    properties: Record<string, any>;
    source?: KnowledgeSource | null;
    confidence?: number;
    version?: number;
}

export interface KnowledgeNeighborNode {
    label: string;
    key: string;
    name: string;
}

export interface KnowledgeRelationship {
    type: string;
    direction: 'out' | 'in';
    node: KnowledgeNeighborNode;
    properties: Record<string, any>;
    confidence?: number;
    // A document's claim awaiting review, and the review that holds it.
    pending?: boolean;
    review_id?: string | null;
}

export interface KnowledgeRecord {
    key: string;
    name: string;
    properties: Record<string, any>;
    members: string[];
    observations: number;
    is_consolidated: boolean;
    sources: KnowledgeSource[];
    // The member whose account of this thing is shown, where someone chose one.
    lead?: string | null;
    ours?: OrganizationRoleName | null;
    // A record a document's pending claims would create ("new") or change ("change").
    pending?: 'new' | 'change';
    review_id?: string | null;
}

// An organization's place in the workspace: its company, or a subsidiary of it.
export type OrganizationRoleName = 'principal' | 'subsidiary';

export interface KnowledgeRecordMember {
    resource_key: string;
    decided_by?: string | null;      // 'rule' | 'person'
    rule?: string | null;            // the identifier that matched
    match_value?: string | null;
    note?: string | null;
    // Whether this member could lead the record: leading breaks ties within a precedence tier.
    can_lead?: boolean;
    observations: KnowledgeNode[];
}

export interface KnowledgeRecordDetail {
    key: string;
    resource: string;
    name: string;
    properties: Record<string, any>;
    contributions: Record<string, { resource_key: string; source?: KnowledgeSource | null }>;
    members: KnowledgeRecordMember[];
    relationships: KnowledgeRelationship[];
    is_consolidated: boolean;
    lead?: string | null;
    ours?: OrganizationRoleName | null;
    suggestions: { id: string; key_a: string; key_b: string; rule?: string | null }[];
    pending?: 'new' | 'change';
    review_id?: string | null;
}

export interface KnowledgeSuggestion {
    id: string;
    label: string;
    rule?: string | null;
    evidence?: Record<string, any>;
    records: { key: string; name: string; properties: Record<string, any> }[];
}

export interface GeneratorReadiness {
    // `data`: a report kept up to date from the record; `draft`: a document an agent writes on request.
    kind: 'data' | 'draft';
    skill?: string | null;
    /** Who writes it, e.g. "Navaris with the Finance analyst skill"; null for a report without commentary. */
    byline?: string | null;
    needs: string[];
    present: string[];
    missing: string[];
    ready: boolean;
    sources: KnowledgeSource[];
    file_id?: string | null;
    linked?: boolean;
    active_job?: { id: string; status: string } | null;
}

export interface GeneratedDocument extends GeneratorReadiness {
    definition_id: string;
    name: string;
    description?: string;
    section_id?: string;
}

export interface FolderDetail {
    source_id: string;
    path: string;
    name: string;
    source?: string | null;
    source_type?: string | null;
    linked_to: FileLinkedTo[];
    candidate_for: FileCandidateFor[];
    folders: string[];
    files: WorkspaceFile[];
}

export interface WorkspaceFile {
    id: string;
    source_id: string;
    source?: string;
    source_type?: string;
    uri: string;
    mime_type: string;
    source_path?: string | null;
    source_filename?: string | null;
    source_metadata?: Record<string, any> | null;
    /** Set while a file is one person's own (a chat upload or chat-written document). */
    owner_user_id?: string | null;
    /** The open review of what this document would add to the record. */
    open_review_id?: string | null;
    version?: number | null;
    // Native timestamps from the originating system; null for snapshot rows and web pages.
    source_created_at?: string | null;
    source_modified_at?: string | null;
    created_at: string;
    updated_at: string;
    linked_to?: FileLinkedTo[];
    candidate_for?: FileCandidateFor[];
    active_job?: { id: string; status: string; task_type?: string } | null;
    linked?: boolean;
}

export interface FileVersion {
    file_id: string;
    version: number;
    title?: string;
    hash: string;
    full_text?: string;
    created_at: string;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  roles: string[];
  is_active: string;
  created_at: string;
  updated_at: string;
}

export interface AuthSettings {
  available_roles: string[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AcceptInviteRequest {
  token: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface HelpLink {
  label: string;
  help_content: string;
}

export interface ImpactDocument {
  id: string;
  name: string;
  priority: string; // required | recommended | optional
}

export interface AnswerImpact {
  value: any;
  label: string;
  includes: ImpactDocument[];
}

export interface Field {
  id: string;
  label: string;
  type: string;
  input_type: string;
  help_text?: string;
  options?: { value: string; label: string; relevant_when?: Record<string, any> }[];
  relevant_when?: Record<string, any>;
  value?: any;
  document_impact?: AnswerImpact[];
  currency?: string;
}

export interface FieldGroup {
  id: string;
  title?: string;
  intro?: string;
  relevant_when?: Record<string, any>;
  help_link?: HelpLink;
  fields: Field[];
}

export interface WorkspaceSection {
  id: string;
  name: string;
  enabled: boolean;
  pending?: boolean;
  subsections?: WorkspaceSection[];
}

export interface ActiveJob {
  id: string;
  task_type?: string | null;
  status: Job['status'];
  source?: string;
  source_type?: string;
}

// `trialing` behaves as `active`; `past_due` and `archived` make the tenant read-only.
export type TenantStatus = 'trialing' | 'active' | 'past_due' | 'archived';

// Presence-only flags are bool; parameterised flags are objects ({max_count: N}). Unknown flag = off.
export type TenantFeatures = Record<string, boolean | Record<string, unknown>>;

export const FEATURE_LIST = {
} as const;

export type FeatureFlag = typeof FEATURE_LIST[keyof typeof FEATURE_LIST];

export type BannerSeverity = 'info' | 'warning' | 'error';

export interface TenantBanner {
  id: string;
  category: string;
  severity: BannerSeverity;
  text: string;
  dismissible: boolean;
  dismissed_at: string | null;
  dismissed_by: string | null;
  expires_at: string | null;
  // Both set or both null; the URL starts with `/` (internal) or `https://` (external, new tab).
  link_url: string | null;
  link_label: string | null;
  created_at: string;
}

export interface WorkspaceOverview {
  name: string;
  has_logo: boolean;
  sections: WorkspaceSection[];
  fields: Record<string, any>;
  active_jobs: ActiveJob[];
  features: TenantFeatures;
  status: TenantStatus;
  banners: TenantBanner[];
  sources: Pick<Source, 'id' | 'source' | 'source_type' | 'updated_at'>[];
  chats: Pick<AgentChatSummary, 'id' | 'title' | 'origin' | 'updated_at'>[];
  agents: Agent[];
  unread_notifications: number;
  review_items: number;
}

export interface WidgetSpec {
  id: string;
  widget: string;
  config?: Record<string, unknown>;
}

export interface WidgetConfigProperty {
  type: 'string' | 'integer' | 'boolean' | 'array';
  title?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  format?: string;
  items?: { type: 'string' };
}

export interface WidgetCatalogueEntry {
  id: string;
  name: string;
  description: string;
  config: { type: 'object'; properties: Record<string, WidgetConfigProperty>; required?: string[] };
}

export interface HomeLayout {
  title: string | null;
  widgets: WidgetSpec[];
  customised: boolean;
  template_changed: boolean;
}

export interface SectionLinkedFile {
  file_id: string;
  version: number;
  latest_version: number;
  source_filename: string;
  source_path: string | null;
  linked_at: string | null;
}

export type DocumentPriority = 'required' | 'recommended' | 'optional';

export interface SectionDocument {
  id: string;
  name: string;
  description?: string;
  notes?: string | null;
  priority: DocumentPriority;
  pending: boolean;
  pending_questions?: { field_id: string; label: string; section_id: string }[];
  linked_files: SectionLinkedFile[];
  candidates_count: number;
}

export interface SectionChild {
  id: string;
  name: string;
  enabled: boolean;
  intro?: string;
}

export interface ModelPeriod {
    start?: string;
    end: string;
    grain: string;
    label?: string;
}

export interface ModelCell {
    key: string;
    concept: string;
    period: ModelPeriod;
    basis: string;
    value: { amount?: number; unit?: string };
    note?: string | null;
    derivation?: { formula: string; inputs?: string[] } | null;
    // `computed` is a formula's own work.
    kind: 'derived' | 'computed' | 'extracted' | 'stated';
}

export interface ModelRow {
    concept: string;
    name: string;
    unit: string;
    kind: 'ledger' | 'metric' | 'measure';
    sign?: string | null;
    depth: number;
    formula?: string | null;
    cells: (ModelCell | null)[];
}

export interface ModelGrid {
    organization: string | null;
    basis: string;
    grain: string;
    columns: ModelPeriod[];
    rows: ModelRow[];
    companies: { key: string; name: string }[];
}

export interface FigureObservation {
    kind: 'derived' | 'computed' | 'extracted' | 'stated';
    value: { amount?: number; unit?: string };
    note?: string | null;
    derivation?: { formula: string; inputs?: string[] } | null;
    observed_at?: string | null;
    source?: KnowledgeSource | null;
}

// Net of tax; a credit note's line is negative.
export interface FigureTransaction {
    key: string;
    description?: string | null;
    amount: { amount?: number; unit?: string | null };
    tax_rate?: string | null;
    on: string;
    document: {
        key: string | null;
        resource: string | null;
        number?: string | null;
        direction?: string | null;
        status?: string | null;
    };
    counterparty?: string | null;
    account: { key: string | null; name?: string | null; code?: string | null };
}

export interface FigureComponent {
    concept: string;
    name: string;
    unit?: string | null;
    sign?: string | null;
    value: { amount?: number; unit?: string };
    kind?: ModelCell['kind'] | null;
    present: boolean;
    // The company a group figure's part belongs to.
    organization?: string | null;
}

export interface FigureWorkings {
    concept: string;
    name: string;
    period: { start: string; end: string };
    nature: 'booked' | 'computed' | 'group';
    total: number;
    transactions: FigureTransaction[];
    components: FigureComponent[];
    records?: { resource: string; key: string; name: string; note?: string | null }[];
    // Every source's figure for this date, the one in force first, and for a line
    // with dimensions its members at that date.
    observations?: FigureObservation[];
    breakdown?: { members: Record<string, string>; value: { amount?: number; unit?: string };
                  kind: string; source?: { name: string | null } | null }[];
    accounts: string[];
}

export interface FigureDetail {
    key: string;
    observations: FigureObservation[];
}

// `decided_by` says whether a rule placed the account or a person did.
export interface AccountMapping {
    key: string;
    name: string;
    code?: string | null;
    account_type?: string | null;
    // Income and expense accounts reach a line; balance-sheet ones come from the system's balance sheet.
    statement: 'profit_and_loss' | 'balance_sheet';
    concept: string | null;
    concept_name: string | null;
    decided_by: 'person' | 'rule' | null;
    suggestion: { id: string; concept: string; concept_name: string | null } | null;
}

export interface AccountsView {
    accounts: AccountMapping[];
    concepts: { id: string; name: string; unit: string; kind: string }[];
}

// The workspace's company, its subsidiaries, and companies to place (GET /knowledge/group).
export interface GroupCompany {
    key: string;
    name: string;
    number?: string | null;
    country?: string | null;
    parent?: string | null;
}

export interface GroupView {
    principal: GroupCompany | null;
    members: GroupCompany[];
    suggestions: GroupCompany[];
}

/** Something Navaris noticed, for the person: GET /notifications. */
export interface AppNotification {
    id: string;
    title: string;
    body: string | null;
    link: string | null;
    kind: 'document_review' | 'document_sync' | 'source_sync' | 'watch' | 'memory' | 'market';
    source_id: string | null;
    created_at: string;
    read_at: string | null;
}

/** One claim of a document review: GET /reviews/{id}. */
export interface ReviewItem {
    id: string;
    kind: 'new' | 'changed' | 'withdrawing';
    row_table: 'fact' | 'edge';
    group_label: string;
    title: string;
    detail: string | null;
    recommendation: 'accept' | 'reject' | 'check' | null;
    reason: string | null;
    status: 'pending' | 'accepted' | 'rejected' | 'superseded';
    /** Who decided it; null for an addition Navaris accepted itself. */
    decided_by: string | null;
}

/** What a linked document would add to the record, awaiting a person. */
export interface DocumentReview {
    id: string;
    file_id: string;
    file_name: string;
    status: 'open' | 'closed' | 'superseded';
    summary: string | null;
    reviewed_at: string | null;
    created_at: string;
    counts?: Record<string, number>;
    items?: ReviewItem[];
}

export interface Watch {
    id: string;
    chat_id: string;
    skill: string | null;
    kind: 'threshold' | 'schedule' | 'due' | 'record';
    spec: Record<string, unknown>;
    /** The watch in words, from its spec. */
    title: string;
    /** When it is checked; a schedule has its next run instead. */
    checked: string | null;
    status: 'active' | 'paused';
    next_run_at: string | null;
    last_fired_at: string | null;
    created_at: string;
}

export interface MemorySource {
    file_id: string;
    version: number;
    name: string;
}

export interface MemorySide {
    passage: string;
    /** "the current text", "the record" or a document's name. */
    source: string;
    /** YYYY-MM-DD, where the source is a document. */
    dated?: string;
}

/** Where a proposal's sources disagree: the passage it kept and the alternative. */
export interface MemoryConflict {
    topic: string;
    kept: MemorySide;
    alternative: MemorySide;
    /** Whether the kept passage stands in the text, so the alternative can be swapped in. */
    switchable: boolean;
    chosen?: 'kept' | 'alternative';
}

export interface MemoryVersion {
    id: string;
    scope: string;
    body: string;
    summary: string | null;
    sources: MemorySource[];
    conflicts: MemoryConflict[];
    status: 'proposed' | 'accepted' | 'rejected' | 'superseded';
    /** "navaris", or the id of the person who wrote it. */
    author: string;
    /** Who accepted it; null for a version Navaris accepted itself. */
    decided_by: string | null;
    created_at: string;
    decided_at: string | null;
}

/** The memory covering an area: `scope` is its own, or the nearest above it. */
export interface Memory {
    scope: string;
    /** The area asked for. */
    area: string;
    name: string;
    guidance: string;
    current: MemoryVersion | null;
    proposal: MemoryVersion | null;
    history: MemoryVersion[];
    /** What people asked Navaris to remember, waiting for the next update. */
    notes: MemoryNote[];
}

export interface MemoryNote {
    id: string;
    text: string;
    /** The area the note is about, which this memory covers. */
    area: string;
    created_at: string;
    by: string | null;
}
